import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { trackNewsletterLead } from '../hooks/useMetaPixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterForm = () => {
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | success | already | error
  const [errorMsg, setErrorMsg] = useState('');

  const canSubmit = consent && email.trim().length > 0 && status !== 'loading';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        language,
        consent_given: true,
      });
      if (res.data?.status === 'already_subscribed') {
        setStatus('already');
      } else {
        setStatus('success');
        setEmail('');
        setConsent(false);
        // Fire Meta Pixel Lead event so ads can optimise for signups.
        trackNewsletterLead();
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(
        err?.response?.data?.detail ||
          t('Er ging iets mis. Probeer het later opnieuw.', 'Something went wrong. Please try again later.', 'Une erreur s\u2019est produite. Veuillez réessayer plus tard.')
      );
    }
  };

  return (
    <div data-testid="newsletter-form-wrapper">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        data-testid="newsletter-form"
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('Je e-mailadres', 'Your email address', 'Votre adresse e-mail')}
          className="flex-1 px-6 py-4 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/40"
          data-testid="newsletter-email-input"
          disabled={status === 'loading'}
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:hover:scale-100 disabled:cursor-not-allowed"
          data-testid="newsletter-subscribe-button"
        >
          {status === 'loading'
            ? t('Bezig...', 'Sending...', 'Envoi...')
            : t('Inschrijven', 'Subscribe', 'S\u2019inscrire')}
        </button>
      </form>

      {/* GDPR consent checkbox */}
      <label
        className="flex items-start gap-3 max-w-md mx-auto mt-4 text-left text-sm text-white/90 cursor-pointer"
        data-testid="newsletter-consent-label"
      >
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-white/40 text-purple-600 focus:ring-white/40 cursor-pointer"
          data-testid="newsletter-consent-checkbox"
        />
        <span>
          {t(
            'Ik ga akkoord met het ontvangen van de nieuwsbrief en de verwerking van mijn gegevens zoals beschreven in het ',
            'I agree to receive the newsletter and to the processing of my data as described in the ',
            "J'accepte de recevoir la newsletter et le traitement de mes données comme décrit dans la "
          )}
          <Link
            to="/privacy"
            className="underline hover:text-white"
            data-testid="newsletter-privacy-link"
          >
            {t('privacybeleid', 'privacy policy', 'politique de confidentialité')}
          </Link>
          {t('.', '.', '.')}
        </span>
      </label>

      {/* Inline feedback */}
      {status === 'success' && (
        <p className="mt-4 text-white font-medium" data-testid="newsletter-success">
          {t(
            'Bedankt! Je staat op de lijst. Tot in onze volgende nieuwsbrief.',
            "Thanks! You're on the list. See you in our next newsletter.",
            "Merci ! Vous êtes sur la liste. À très bientôt dans notre prochaine newsletter."
          )}
        </p>
      )}
      {status === 'already' && (
        <p className="mt-4 text-white/90 font-medium" data-testid="newsletter-already">
          {t(
            'Je bent al ingeschreven. Bedankt voor je interesse!',
            "You're already subscribed. Thanks for your interest!",
            'Vous êtes déjà inscrit. Merci pour votre intérêt !'
          )}
        </p>
      )}
      {status === 'error' && (
        <p className="mt-4 text-pink-100 font-medium" data-testid="newsletter-error">
          {errorMsg}
        </p>
      )}
    </div>
  );
};

export default NewsletterForm;
